import { Fragment, useState, type ReactNode } from 'react';
import type { LucideIcon } from 'lucide-react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import {
	Menu,
	X,
	ChevronDown,
	ChevronUp,
	UserRound,
	LogIn,
	Brain,
	Stethoscope,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { getUserData } from '@/utils/cookies';
import { E2E_HEADER } from '@/constants/e2eSchedulingTestIds';

type HeaderProps = {
	/** Quando `false`, oculta divisor + alternância Empresas / Planos individuais. */
	showAudienceSwitcher?: boolean;
	/** Substitui o bloco “Entrar” (desktop e fluxo mobile simplificado). */
	trailing?: ReactNode;
	/** Clique no logo: path fixo (ex. formulário colaborador → `/`). */
	logoHomePath?: string;
};

const Header = ({
	showAudienceSwitcher = true,
	trailing,
	logoHomePath,
}: HeaderProps = {}) => {
	const logoProfessional =
		'https://res.cloudinary.com/dd5pbp2tb/image/upload/v1770986601/Plumaa_H_psjyrs.png';
	const logoHeaderWhite =
		'https://res.cloudinary.com/dd5pbp2tb/image/upload/v1770986600/Plumaa_H_W_hribid.png';
	const navigate = useNavigate();
	const location = useLocation();
	const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
	const [isDropdownOpen, setIsDropdownOpen] = useState(false);

	const isBusinessPage = location.pathname === '/empresa';
	const isPsychologistPage = location.pathname === '/psicologo';
	const isCollaboratorsPage = location.pathname === '/colaboradores';
	const isPatientsPage =
		location.pathname === '/' || location.pathname === '/pacientes';
	const audienceParam = new URLSearchParams(location.search).get('audience');
	type AudienceId = 'b2b' | 'paciente' | 'psicologo' | 'b2b2c';
	const activeAudienceId: AudienceId | null = isBusinessPage
		? 'b2b'
		: isPsychologistPage
		  ? 'psicologo'
		  : isPatientsPage
		    ? 'paciente'
		    : isCollaboratorsPage
		      ? audienceParam === 'paciente'
						? 'paciente'
						: 'b2b2c'
		      : null;

	const audienceNavItems: { id: AudienceId; label: string }[] = [
		{ id: 'b2b', label: 'Empresas' },
		{ id: 'paciente', label: 'Planos individuais' },
		{ id: 'psicologo', label: 'Psicólogo' },
	];

	const visibleAudienceNavItems = audienceNavItems.filter(
		(item) => item.id !== activeAudienceId,
	);

	const userData = getUserData();
	const isUserComplete = Boolean(userData?.is_complete);

	const loginOptions: {
		id: string;
		label: string;
		path: string;
		Icon: LucideIcon;
		testId?: string;
	}[] = [
		{
			id: 'cliente-plumaa',
			label: 'Cliente Plumaa',
			path: '/login',
			Icon: UserRound,
			testId: E2E_HEADER.LOGIN_COLLABORATOR,
		},
		{
			id: 'psicologo',
			label: 'Psicólogo',
			path: '/profissional/login',
			Icon: Brain,
		},
		{
			id: 'medico',
			label: 'Médico',
			path: '/profissional/login',
			Icon: Stethoscope,
		},
	];

	const navigateToHome = () => {
		if (logoHomePath) return navigate(logoHomePath);
		if (activeAudienceId === 'b2b') return navigate('/empresa');
		if (activeAudienceId === 'psicologo') return navigate('/psicologo');
		if (activeAudienceId === 'paciente') {
			return navigate('/');
		}
		return navigate('/colaboradores');
	};

	const toggleDropdown = () => setIsDropdownOpen(!isDropdownOpen);

	const navigateToAudience = (audienceId: AudienceId) => {
		const url =
			audienceId === 'b2b'
				? '/empresa'
				: audienceId === 'psicologo'
				  ? '/psicologo'
				  : audienceId === 'paciente'
				    ? '/'
				    : '/colaboradores';

		navigate(url);
		window.scrollTo({ top: 0, behavior: 'smooth' });
	};

	const usePublicLoginChrome = trailing == null;
	const hideMobileDrawer = !showAudienceSwitcher && !usePublicLoginChrome;

	return (
		<>
			<header className="sticky top-0 z-50 flex h-16 w-full items-center justify-center border-b border-zinc-200/50 bg-white px-4">
				<div className="flex w-full max-w-7xl items-center justify-between">
					<div className="flex items-center gap-6">
						<div
							onClick={navigateToHome}
							className="flex cursor-pointer items-center"
						>
							<img
								src={logoProfessional}
								className="h-8 w-auto md:h-10"
								alt="Logotipo Plumaa"
							/>
						</div>

						{showAudienceSwitcher ? (
							<div className="hidden items-center gap-6 md:flex">
								<div className="h-6 w-px bg-zinc-300"></div>

								<div className="flex items-center gap-6">
									{visibleAudienceNavItems.map((item, index) => (
										<Fragment key={item.id}>
											{index > 0 ? (
												<span
													className="select-none text-zinc-300"
													aria-hidden="true"
												>
													|
												</span>
											) : null}
											<button
												type="button"
												data-label={item.label}
												onClick={() =>
													navigateToAudience(item.id)
												}
												className={cn(
													'inline-flex items-center text-sm font-medium leading-none outline-none transition-colors hover:text-zinc-950 lg:text-base',
													'text-zinc-600 hover:text-zinc-950',
												)}
											>
												{item.label}
											</button>
										</Fragment>
									))}
								</div>
							</div>
						) : null}
					</div>

					<div className="flex items-center gap-4">
						{usePublicLoginChrome ? (
							<>
								{isUserComplete ? (
									<button
										type="button"
										onClick={() => navigate('/home')}
										className="hidden items-center gap-1 text-sm font-medium leading-none text-zinc-500 transition-colors hover:text-zinc-800 md:inline-flex"
									>
										<span>Entrar</span>
										<LogIn
											size={16}
											className="text-zinc-400"
										/>
									</button>
								) : (
									<div className="relative hidden md:flex">
										<div className="relative">
											<button
												type="button"
												data-testid={
													E2E_HEADER.OPEN_LOGIN_MENU
												}
												onClick={toggleDropdown}
												onBlur={() =>
													setTimeout(
														() =>
															setIsDropdownOpen(
																false,
															),
														200,
													)
												}
												className="inline-flex items-center gap-1 text-sm font-medium leading-none text-zinc-500 transition-colors hover:text-zinc-800 focus:outline-none"
											>
												Entrar
												{isDropdownOpen ? (
													<ChevronUp size={16} />
												) : (
													<ChevronDown size={16} />
												)}
											</button>

											<div
												className={cn(
													'absolute right-0 top-full mt-2 w-52 origin-top-right overflow-hidden rounded-md border border-zinc-200 bg-white shadow-lg transition-all duration-200',
													isDropdownOpen
														? 'visible scale-100 opacity-100'
														: 'invisible scale-95 opacity-0',
												)}
											>
												<div className="flex flex-col py-1">
													{loginOptions.map(
														(option) => {
															const Icon =
																option.Icon;
															return (
																<Link
																	key={
																		option.id
																	}
																	to={
																		option.path
																	}
																	data-testid={
																		option.testId
																	}
																	className="flex items-center gap-2.5 px-4 py-2 text-left text-sm text-zinc-600 hover:bg-zinc-50 hover:text-brand-pink"
																>
																	<Icon
																		className="h-4 w-4 shrink-0 text-zinc-400"
																		strokeWidth={
																			2
																		}
																		aria-hidden
																	/>
																	<span>
																		{
																			option.label
																		}
																	</span>
																</Link>
															);
														},
													)}
												</div>
											</div>
										</div>
									</div>
								)}

								<button
									type="button"
									data-testid={E2E_HEADER.MOBILE_MENU_OPEN}
									onClick={() => setIsMobileMenuOpen(true)}
									className="rounded-md p-2 text-brand-pink hover:bg-zinc-100 focus:outline-none md:hidden"
								>
									<Menu className="h-6 w-6" />
								</button>
							</>
						) : (
							<div className="flex max-w-[min(200px,42vw)] items-center justify-end">
								{trailing}
							</div>
						)}
					</div>
				</div>
			</header>

			{/* --- SIDEBAR MOBILE --- */}
			<div
				className={cn(
					'pointer-events-none fixed inset-0 z-[60] flex justify-end transition-opacity duration-300 md:hidden',
					hideMobileDrawer && 'hidden',
					isMobileMenuOpen
						? 'pointer-events-auto opacity-100'
						: 'opacity-0',
				)}
			>
				<div
					className="absolute inset-0 bg-black/50 backdrop-blur-sm"
					onClick={() => setIsMobileMenuOpen(false)}
				/>

				<div
					className={cn(
						'relative flex h-full w-72 max-w-[320px] transform flex-col bg-white shadow-2xl transition-transform duration-300 ease-in-out',
						isMobileMenuOpen ? 'translate-x-0' : 'translate-x-full',
					)}
				>
					<div className="flex items-center justify-between border-b border-zinc-800/60 bg-stone-900 px-4 py-4">
						<img
							src={logoHeaderWhite}
							alt="Logotipo Plumaa"
							className="h-7 w-auto"
						/>
						<button
							onClick={() => setIsMobileMenuOpen(false)}
							className="rounded-full p-1.5 text-zinc-300 transition-colors hover:bg-white/10 hover:text-white"
						>
							<X className="h-5 w-5" />
						</button>
					</div>

					<nav className="flex flex-1 flex-col overflow-y-auto px-4 py-5 text-sm text-zinc-700">
						<ul className="space-y-1">
							{visibleAudienceNavItems.map((item) => (
								<li key={item.id}>
									<button
										type="button"
										onClick={() => {
											navigateToAudience(item.id);
											setIsMobileMenuOpen(false);
										}}
										className={cn(
											'flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left text-sm font-medium transition-colors hover:bg-zinc-50 hover:text-zinc-950',
											'text-zinc-600',
										)}
									>
										<span>{item.label}</span>
									</button>
								</li>
							))}

							<>
								{isUserComplete ? (
									<li>
										<button
											type="button"
											onClick={() => {
												navigate('/home');
												setIsMobileMenuOpen(false);
											}}
											className="flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-zinc-600 transition-colors hover:bg-zinc-50"
										>
											<span className="font-medium">
												Entrar
											</span>
											<LogIn className="h-4 w-4 text-zinc-400" />
										</button>
									</li>
								) : (
									<>
										<li className="text-xs font-semibold uppercase tracking-[0.14em] text-zinc-400">
											Entrar
										</li>

										{loginOptions.map((option) => {
											const Icon = option.Icon;
											return (
												<li key={option.id}>
													<Link
														to={option.path}
														onClick={() =>
															setIsMobileMenuOpen(
																false,
															)
														}
														data-testid={
															option.id ===
															'cliente-plumaa'
																? E2E_HEADER.LOGIN_COLLABORATOR_MOBILE
																: undefined
														}
														className="flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-zinc-600 transition-colors hover:bg-zinc-50"
													>
														<Icon
															className="h-4 w-4 shrink-0 text-zinc-400"
															strokeWidth={2}
															aria-hidden
														/>
														<span>
															{option.label}
														</span>
													</Link>
												</li>
											);
										})}
									</>
								)}
							</>
						</ul>
					</nav>
				</div>
			</div>
		</>
	);
};

export { Header };
